import * as R from 'ramda';
import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import storage from '../../../../../../../../hooks/useStorage/useStorage';
import { ProductDetailsFilterDeliveryWindow } from '../../../../../../../../services/modules/productDetails/productDetails.types';
import useProductDetailsStore from '../../../../../../../../stores/ProductDetails/productDetails.store';
import { CompactAvailability } from '../../../../../../ProductTypes';
import { toggleAvailabilityGridParams } from './useAvailabilityFilter.utils';

interface UseAvailabilityFilterStateParams {
  deliveryWindows: ProductDetailsFilterDeliveryWindow[];
  availabilities: CompactAvailability[];
}

export const useAvailabilityFilterState = ({
  deliveryWindows,
  availabilities,
}: UseAvailabilityFilterStateParams) => {
  const { productGridParams, handleChangeProductGridParams, removeCopiedGrid } =
    useProductDetailsStore(
      useShallow((s) => ({
        productGridParams: s.productGridParams,
        handleChangeProductGridParams: s.handleChangeProductGridParams,
        removeCopiedGrid: s.removeCopiedGrid,
      })),
    );

  const { availability, deliveryWindowIds, deliveryWindowsSnapshot } = productGridParams;

  const uniqueDeliveryWindows = R.uniqBy(R.prop('id'), deliveryWindows);
  const uniqueDeliveryWindowIds = uniqueDeliveryWindows.map((dw) => dw.id);

  const isAllAvailabilitiesSelected = R.isNil(availability);
  const isPreOrderSelected =
    isAllAvailabilitiesSelected || availability === CompactAvailability.PRE_ORDER;
  const isReadyForDeliverySelected =
    isAllAvailabilitiesSelected || availability === CompactAvailability.READY_FOR_DELIVERY;

  const deliveryWindowsForDropdown = useMemo(() => {
    const currentIds = new Set(uniqueDeliveryWindowIds);
    const committedIds = deliveryWindowIds ?? [];
    const snapshot = deliveryWindowsSnapshot ?? [];

    const missingWindows: ProductDetailsFilterDeliveryWindow[] = committedIds
      .filter((id) => !currentIds.has(id))
      .map((id) => ({
        id,
        name: snapshot.find((dw) => dw.id === id)?.name ?? id,
        productColorId: '',
      }));

    return [...uniqueDeliveryWindows, ...missingWindows];
  }, [uniqueDeliveryWindows, deliveryWindowIds, deliveryWindowsSnapshot]);

  const [pendingWindows, setPendingWindows] = useState<string[] | null>(null);

  const getCommittedWindows = (): string[] => {
    if (deliveryWindowIds) {
      return deliveryWindowIds;
    }
    return isPreOrderSelected ? uniqueDeliveryWindowIds : [];
  };

  const selectedDeliveryWindows = pendingWindows ?? getCommittedWindows();

  const persistAvailabilityFilter = (newAvailability: CompactAvailability | undefined) => {
    storage.setProductDetailsFilter(storage.getSessionId() as string, {
      availabilityFilter: newAvailability,
    });
  };

  const updateAvailability = (value: CompactAvailability) => {
    if (value === availability || !availabilities.includes(value)) {
      return;
    }

    removeCopiedGrid();

    const nextGridParams = toggleAvailabilityGridParams({
      toggled: value,
      current: productGridParams,
    });

    handleChangeProductGridParams({ ...productGridParams, ...nextGridParams });

    persistAvailabilityFilter(nextGridParams.availability);
  };

  const toggleWindow = (id: string) => {
    const current = pendingWindows ?? getCommittedWindows();
    const isSelected = current.includes(id);

    if (!isSelected) {
      setPendingWindows([...current, id]);
      return;
    }

    const isRemovingLastWindow = current.length === 1;
    if (isRemovingLastWindow && !isReadyForDeliverySelected) {
      return;
    }

    setPendingWindows(current.filter((w) => w !== id));
  };

  const handleToggleAllWindows = () => {
    const current = pendingWindows ?? getCommittedWindows();
    const isAllCurrentSelected =
      uniqueDeliveryWindowIds.length > 0 &&
      current.length === uniqueDeliveryWindowIds.length &&
      uniqueDeliveryWindowIds.every((id) => current.includes(id));

    if (!isReadyForDeliverySelected && isAllCurrentSelected) {
      return;
    }

    const getBulkAvailability = (): CompactAvailability | undefined => {
      if (isAllCurrentSelected) {
        return CompactAvailability.READY_FOR_DELIVERY;
      }
      if (isReadyForDeliverySelected) {
        return undefined;
      }
      return CompactAvailability.PRE_ORDER;
    };

    const newAvailability = getBulkAvailability();

    removeCopiedGrid();
    setPendingWindows(null);
    handleChangeProductGridParams({
      ...productGridParams,
      availability: newAvailability,
      deliveryWindowIds: undefined,
      deliveryWindowsSnapshot: undefined,
    });
    persistAvailabilityFilter(newAvailability);
  };

  const applyDeliveryWindows = () => {
    if (pendingWindows === null) {
      return;
    }

    const committed = getCommittedWindows();
    const hasChanged = !R.equals([...pendingWindows].sort(), [...committed].sort());
    setPendingWindows(null);
    if (!hasChanged) {
      return;
    }

    removeCopiedGrid();

    const isDeselectingAllWindows = pendingWindows.length === 0;

    const getNewAvailability = (): CompactAvailability | undefined => {
      if (isDeselectingAllWindows) {
        return CompactAvailability.READY_FOR_DELIVERY;
      }
      if (isReadyForDeliverySelected) {
        return undefined;
      }
      return CompactAvailability.PRE_ORDER;
    };

    const newAvailability = getNewAvailability();
    const newDeliveryWindows = isDeselectingAllWindows ? undefined : pendingWindows;
    const newSnapshot = newDeliveryWindows?.map((id) => ({
      id,
      name: deliveryWindowsForDropdown.find((dw) => dw.id === id)?.name ?? id,
      productColorId: '',
    }));

    handleChangeProductGridParams({
      ...productGridParams,
      availability: newAvailability,
      deliveryWindowIds: newDeliveryWindows,
      deliveryWindowsSnapshot: newSnapshot,
    });

    persistAvailabilityFilter(newAvailability);
  };

  const isPreOrderDisabled = !availabilities.includes(CompactAvailability.PRE_ORDER);
  const isReadyForDeliveryDisabled = !availabilities.includes(
    CompactAvailability.READY_FOR_DELIVERY,
  );

  return {
    isPreOrderSelected,
    selectedDeliveryWindows,
    productGridParamsDeliveryWindowIds: deliveryWindowIds ?? [],
    isReadyForDeliverySelected,
    uniqueDeliveryWindows,
    deliveryWindowsForDropdown,
    isAllAvailabilitiesSelected,
    isPreOrderDisabled,
    isReadyForDeliveryDisabled,
    toggleWindow,
    updateAvailability,
    applyDeliveryWindows,
    handleToggleAllWindows,
  };
};
